import { isDebugServiceFetches } from './debugServiceFetches';
import { devInfo } from './devLog';

export type NetworkCallClass = 'push' | 'pull' | 'online' | 'realtime' | 'auth';

export type NetworkCallCategory =
  | 'auth'
  | 'session_bootstrap'
  | 'entitlements'
  | 'feature_flags'
  | 'catalog_snapshot'
  | 'customers_snapshot'
  | 'cash_summary'
  | 'reports'
  | 'sales_read'
  | 'realtime'
  | 'push_flush'
  | 'support'
  | 'fiscal'
  | 'branches'
  | 'employees'
  | 'other';

export type NetworkCallEntry = {
  class: NetworkCallClass;
  category: NetworkCallCategory;
  name: string;
  trigger?: string;
  cached?: boolean;
  at: number;
};

const sessionStartedAt = Date.now();
const entries: NetworkCallEntry[] = [];

function classForCategory(category: NetworkCallCategory): NetworkCallClass {
  switch (category) {
    case 'catalog_snapshot':
    case 'customers_snapshot':
      return 'pull';
    case 'push_flush':
      return 'push';
    case 'realtime':
      return 'realtime';
    case 'auth':
    case 'session_bootstrap':
      return 'auth';
    default:
      return 'online';
  }
}

/** Registra una llamada de red en sesión (dev / EXPO_PUBLIC_DEBUG_SERVICE_FETCHES=1). */
export function recordNetworkCall(input: {
  class?: NetworkCallClass;
  category: NetworkCallCategory;
  name: string;
  trigger?: string;
  cached?: boolean;
}): void {
  if (!isDebugServiceFetches()) return;
  entries.push({
    class: input.class ?? classForCategory(input.category),
    category: input.category,
    name: input.name,
    trigger: input.trigger,
    cached: input.cached,
    at: Date.now(),
  });
}

/** Compat con startupNetworkDebug existente. */
export function recordStartupNetworkCall(
  category: NetworkCallCategory,
  label: string,
  opts?: { cached?: boolean; trigger?: string }
): void {
  recordNetworkCall({
    category,
    name: label,
    cached: opts?.cached,
    trigger: opts?.trigger,
  });
}

export function logSessionNetworkSummary(): void {
  if (!__DEV__ || !isDebugServiceFetches() || entries.length === 0) return;

  const byClass = new Map<NetworkCallClass, number>();
  const byCategory = new Map<NetworkCallCategory, string[]>();
  let cachedHits = 0;

  for (const e of entries) {
    byClass.set(e.class, (byClass.get(e.class) ?? 0) + 1);
    const list = byCategory.get(e.category) ?? [];
    list.push(e.cached ? `${e.name} (cached)` : e.name);
    byCategory.set(e.category, list);
    if (e.cached) cachedHits += 1;
  }

  const lines: string[] = [
    `[networkAudit] resumen sesión (+${Date.now() - sessionStartedAt}ms)`,
    `  total: ${entries.length} | cached: ${cachedHits}`,
  ];
  for (const [cls, n] of byClass) {
    lines.push(`  ${cls}: ${n}`);
  }
  for (const [cat, labels] of byCategory) {
    if (!labels.length) continue;
    lines.push(`  ${cat}: ${labels.length} — ${labels.slice(0, 8).join(', ')}${labels.length > 8 ? '…' : ''}`);
  }
  devInfo(lines.join('\n'));
}

export function resetNetworkCallAudit(): void {
  entries.length = 0;
}
